import React, { useState, useEffect, useCallback } from 'react';
import { api, openIspis, getPriloziUrl, Narudzbenica } from '../api';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import NarudzbenicaModal from '../components/NarudzbenicaModal';
import PregledModal from '../components/PregledModal';
import 'bootstrap/dist/css/bootstrap.min.css';

function formatHr(num: number): string {
  return num.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

export default function Narudzbenice() {
  const [data, setData] = useState<Awaited<ReturnType<typeof api.narudzbenice.list>> | null>(null);
  const [filterGodina, setFilterGodina] = useState(new Date().getFullYear());
  const [filterOdjel, setFilterOdjel] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPregled, setShowPregled] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [pregledId, setPregledId] = useState<number | null>(null);
  const [csrfToken, setCsrfToken] = useState('');
  const [ispisLoading, setIspisLoading] = useState<number | null>(null);

  const { user } = useAuth();
  const isPromatrac = user?.role === 'promatrac';
  const isAdmin = user?.role === 'administrator';

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params: { filter_godina?: number; filter_odjel?: number } = { filter_godina: filterGodina };
      if (filterOdjel) params.filter_odjel = Number(filterOdjel);
      const res = await api.narudzbenice.list(params);
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Greška');
    } finally {
      setLoading(false);
    }
  }, [filterGodina, filterOdjel]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    api.auth.csrf().then((r) => setCsrfToken(r.csrf_token)).catch(() => {});
  }, []);

  const handleSaved = () => {
    setShowModal(false);
    setEditId(null);
    setSuccess('Narudžbenica spremljena');
    loadData();
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleView = (id: number) => {
    setPregledId(id);
    setShowPregled(true);
  };

  const handleDelete = async (n: Narudzbenica) => {
    if (!window.confirm(`Obrisati narudžbenicu ${n.broj_narudzbenice}? OVO JE TRAJNO!`)) return;
    try {
      await api.narudzbenice.delete(n.id, csrfToken);
      setSuccess('Narudžbenica obrisana');
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Greška');
    }
  };

  const handleStorno = async (n: Narudzbenica) => {
    const razlog = window.prompt('Razlog storniranja:');
    if (!razlog?.trim()) return;
    if (!window.confirm('Stornirati narudžbenicu?')) return;
    try {
      await api.narudzbenice.storno(n.id, razlog, csrfToken);
      setSuccess('Narudžbenica stornirana');
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Greška');
    }
  };

  const handleReaktiviraj = async (n: Narudzbenica) => {
    if (!window.confirm('Reaktivirati narudžbenicu?')) return;
    try {
      await api.narudzbenice.reaktiviraj(n.id, csrfToken);
      setSuccess('Narudžbenica reaktivirana');
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Greška');
    }
  };

  const handleIspis = async (id: number) => {
    setIspisLoading(id);
    try {
      await openIspis(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Greška pri ispisu');
    } finally {
      setIspisLoading(null);
    }
  };

  const handleEmail = async (n: Narudzbenica) => {
    const email = window.prompt('Email primatelja:');
    if (!email) return;
    try {
      await api.email.send(n.id, email);
      alert('Email poslan');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Greška');
    }
  };

  if (!data) {
    return (
      <Layout>
        <div className="text-center py-5">{loading ? 'Učitavanje...' : error || 'Nema podataka'}</div>
      </Layout>
    );
  }

  const { narudzbenice, dostupne_godine, zadnja_id_za_godinu, odjeli_filter } = data;

  return (
    <Layout>
      {success && (
        <div className="alert alert-success alert-dismissible">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess('')} />
        </div>
      )}
      {error && (
        <div className="alert alert-danger alert-dismissible">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')} />
        </div>
      )}

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0">Popis narudžbenica</h5>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <select
              className="form-select form-select-sm"
              style={{ width: 100 }}
              value={filterGodina}
              onChange={(e) => setFilterGodina(Number(e.target.value))}
            >
              {dostupne_godine.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            {(isPromatrac || isAdmin) && odjeli_filter.length > 0 && (
              <select
                className="form-select form-select-sm"
                style={{ width: 250 }}
                value={filterOdjel}
                onChange={(e) => setFilterOdjel(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">-- Svi odjeli --</option>
                {odjeli_filter.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.sifra} - {o.naziv}
                  </option>
                ))}
              </select>
            )}
            {!isPromatrac && (
              <button className="btn btn-success" onClick={() => { setEditId(null); setShowModal(true); }}>
                + Nova narudžbenica
              </button>
            )}
          </div>
        </div>
        <div className="card-body">
          <div className="alert alert-info">
            Godina <strong>{filterGodina}</strong> (ukupno: <strong>{narudzbenice.length}</strong>)
          </div>
          <div className="table-responsive">
            <table className="table table-striped table-hover table-sm">
              <thead>
                <tr>
                  <th>Broj</th>
                  <th>Tip</th>
                  <th>Datum</th>
                  {isPromatrac && <th>Odjel</th>}
                  <th>Kupac</th>
                  <th>Isporučitelj</th>
                  <th className="text-end">Ukupno (€)</th>
                  <th>Status</th>
                  <th>Akcije</th>
                </tr>
              </thead>
              <tbody>
                {narudzbenice.length === 0 ? (
                  <tr>
                    <td colSpan={isPromatrac ? 9 : 8} className="text-center text-muted">
                      Nema narudžbenica
                    </td>
                  </tr>
                ) : (
                  narudzbenice.map((n) => {
                    const jeZadnja = !isPromatrac && zadnja_id_za_godinu === n.id;
                    const jeStornirana = n.stornirana === 1;
                    const tipBadge =
                      n.tip_nabave === 'radovi' ? 'bg-warning text-dark' :
                      n.tip_nabave === 'usluge' ? 'bg-info' : 'bg-primary';
                    return (
                      <React.Fragment key={n.id}>
                        <tr className={jeStornirana ? 'table-secondary' : ''}>
                          <td>
                            <strong>{n.broj_narudzbenice}</strong>
                            {jeStornirana && <><br /><span className="badge bg-danger">STORNO</span></>}
                          </td>
                          <td>
                            <span className={`badge ${tipBadge}`}>
                              {n.tip_nabave === 'radovi' ? 'RAD' : n.tip_nabave === 'usluge' ? 'USL' : 'ROBA'}
                            </span>
                          </td>
                          <td>{new Date(n.datum).toLocaleDateString('hr-HR')}</td>
                          {isPromatrac && <td><span className="badge bg-secondary">{n.odjel_sifra || 'N/A'}</span></td>}
                          <td>{n.kupac_naziv}</td>
                          <td>{n.isporucitelj_naziv}</td>
                          <td className="text-end">
                            {jeStornirana ? <del>{formatHr(Number(n.ukupno_s_pdv))}</del> : formatHr(Number(n.ukupno_s_pdv))}
                          </td>
                          <td>
                            {jeStornirana ? (
                              <span className="badge bg-danger">Stornirana</span>
                            ) : (
                              <span className="badge bg-info">{n.status}</span>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-1 flex-wrap">
                              <button className="btn btn-sm btn-info" onClick={() => handleView(n.id)} title="Pregled">
                                <i className="bi bi-eye" />
                              </button>
                              <button className="btn btn-sm btn-primary" onClick={() => handleIspis(n.id)} disabled={ispisLoading === n.id} title="Ispiši">
                                <i className="bi bi-printer" /> {ispisLoading === n.id ? '...' : 'Ispiši'}
                              </button>
                              <a href={getPriloziUrl(n.id)} className="btn btn-sm btn-secondary" title="Prilozi">
                                <i className="bi bi-paperclip" />
                              </a>
                              <button className="btn btn-sm btn-success" onClick={() => handleEmail(n)} title="Email">
                                <i className="bi bi-envelope" /> Email
                              </button>
                              {!isPromatrac && !jeStornirana && (
                                <>
                                  <button className="btn btn-sm btn-warning" onClick={() => { setEditId(n.id); setShowModal(true); }} title="Ispravi">
                                    <i className="bi bi-pencil" /> Ispravi
                                  </button>
                                  {jeZadnja ? (
                                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(n)} title="Obriši">
                                      <i className="bi bi-trash" /> Obriši
                                    </button>
                                  ) : (
                                    <button className="btn btn-sm btn-danger" onClick={() => handleStorno(n)} title="Storniraj">
                                      <i className="bi bi-x-circle" /> Storniraj
                                    </button>
                                  )}
                                </>
                              )}
                              {!isPromatrac && jeStornirana && (
                                <button className="btn btn-sm btn-success" onClick={() => handleReaktiviraj(n)} title="Reaktiviraj">
                                  <i className="bi bi-arrow-counterclockwise" /> Reaktiviraj
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                        {jeStornirana && n.storno_napomena && (
                          <tr className="table-secondary">
                            <td colSpan={isPromatrac ? 9 : 8} className="small">
                              <strong>Razlog storna:</strong> {n.storno_napomena}
                              {n.storno_datum && (
                                <em className="text-muted ms-2">
                                  ({new Date(n.storno_datum).toLocaleString('hr-HR')})
                                </em>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showModal && (
        <NarudzbenicaModal
          editId={editId}
          data={data}
          filterGodina={filterGodina}
          onClose={() => { setShowModal(false); setEditId(null); }}
          onSaved={handleSaved}
        />
      )}

      {showPregled && pregledId && (
        <PregledModal
          id={pregledId}
          onClose={() => { setShowPregled(false); setPregledId(null); }}
        />
      )}
    </Layout>
  );
}
